import { useLayoutEffect, useRef } from 'react';

// Kept within the mounted layout and scoped to its user; no cross-account storage.
export default function useScrollMemory(ref, route, userId) {
  const positions = useRef(new Map());
  useLayoutEffect(() => {
    const main = ref.current;
    if (!main) return;
    const key = `${userId}:${route}`;
    const target = positions.current.get(key) || 0;
    let restoring = target > 0, frame, owner;
    const findOwner = () => main.querySelector('[data-polka-scroll]') || main;
    const restore = () => {
      owner = findOwner();
      if (restoring || !target) owner.scrollTop = target;
      if (owner.scrollHeight - owner.clientHeight >= target) restoring = false;
    };
    restore();
    const save = event => {
      if (event.target !== findOwner() || restoring) return;
      positions.current.set(key, event.target.scrollTop);
      if (positions.current.size > 60) positions.current.delete(positions.current.keys().next().value);
    };
    const interrupted = () => { restoring = false; };
    const observer = new MutationObserver(() => {
      // Lazy routes can replace the initial main scroll area after mounting.
      if (findOwner() !== owner || restoring) { cancelAnimationFrame(frame); frame = requestAnimationFrame(restore); }
    });
    observer.observe(main, { childList: true, subtree: true });
    const resize = new ResizeObserver(() => { if (restoring) restore(); });
    resize.observe(main);
    const onLoad = () => { if (restoring) restore(); };
    main.addEventListener('load', onLoad, true);
    main.addEventListener('scroll', save, true);
    for (const event of ['touchstart', 'wheel', 'pointerdown']) main.addEventListener(event, interrupted, { passive: true });
    const timer = setTimeout(interrupted, 5000);
    return () => {
      observer.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); clearTimeout(timer);
      main.removeEventListener('scroll', save, true); main.removeEventListener('load', onLoad, true);
      for (const event of ['touchstart', 'wheel', 'pointerdown']) main.removeEventListener(event, interrupted);
    };
  }, [ref, route, userId]);
}
